"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";

import { CTAButton } from "@/shadcn/ui/button";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

import gridBGimg from "@/public/lp2/dear/grid.svg";
import blurImg from "@/public/lp2/dear/blur.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function DearSection({ data }: { data: PageHomeSchema }) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const dearTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      dearTL.from(content1Ref?.current?.children, {
        y: "1rem",
        stagger: 0.1,
        delay: 0.25,
        scrollTrigger: {
          trigger: content1Ref?.current,
          end: "center center",
          scrub: 1,
        },
      });
    },
    { scope: containerRef }
  );

  return (
    <section ref={containerRef} className="w-full">
      <div className="container-lp mx-auto pt-[16rem] lg:pt-[30rem] pb-[20rem]">
        <div className="flex flex-col items-start justify-center w-full md:w-[70%] lg:w-[46%] mx-auto">
          <div ref={content1Ref} className="mb-[2rem]">
            <div className="mb-[6rem] w-full relative">
              {/* grid */}
              <div className="w-[150%] absolute left-[10%] top-[0%] translate-x-[-50%] translate-y-[-50%] z-[0]">
                <Image
                  src={gridBGimg}
                  alt="grid background"
                  className="w-full"
                />
              </div>
              {/* shadow/blur */}
              <div className="w-full absolute left-[10%] top-[0%] translate-x-[-50%] translate-y-[-50%] z-[0]">
                <Image
                  src={blurImg}
                  alt="blur background"
                  className="dear-section-blur w-full"
                />
              </div>
              {/*  */}
              {data?.dearTitle && (
                <h3 className="text-white text-[4.5rem] font-[700] leading-[3.125rem] relative z-[1]">
                  {data?.dearTitle}
                </h3>
              )}
            </div>
            {data?.dearTexts1 && (
              <PortableText
                value={data?.dearTexts1 ?? []}
                components={customRichTextComponent({
                  block: {
                    normal: ({ children }) => (
                      <p className="text-[#ffffffbf] text-[2.5rem] font-[400] leading-[150%] mb-[3rem]">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    strong: ({ children }) => (
                      <span className="font-[700] text-white">{children}</span>
                    ),
                  },
                  list: {
                    bullet: ({ children }) => (
                      <ul className="list-disc list-green-indicator ms-[3rem]">
                        {children}
                      </ul>
                    ),
                    number: ({ children }) => <ol className="">{children}</ol>,
                  },
                  listItem: {
                    bullet: ({ children }) => (
                      <li className="w-full text-[#ffffffbf] text-[2.5rem] font-[400] leading-[150%] border-b border-b-white/10 py-[2rem] last:border-b-0">
                        {children}
                      </li>
                    ),
                  },
                })}
              />
            )}

            <span className="text-white text-center text-[2.5rem] font-[600] leading-[150%] my-[3rem] w-full block">
              {data?.dearComponentsTitle}
            </span>

            <div className="w-full grid grid-cols-2 items-stretch gap-[2.4rem] mb-[5rem]">
              {data?.dearComponents?.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col justify-center items-center gap-[2rem] py-[3rem] px-[2.4rem] rounded-[1rem] bg-[#ffffff0d] transition duration-300 ease-in-out hover:scale-[1.02]"
                >
                  {item?.icon && <GroqImage src={item.icon} alt="icon" />}
                  <span className="text-white text-center text-[2.5rem] font-[500] leading-[130%] tracking-[-0.1750rem] block lg:px-[0.5rem]">
                    {item.title}
                  </span>
                </div>
              ))}
            </div>

            {data?.dearTexts2 && (
              <PortableText
                value={data?.dearTexts2 ?? []}
                components={customRichTextComponent({
                  block: {
                    normal: ({ children }) => (
                      <p className="text-[#ffffffbf] text-[2.5rem] font-[400] leading-[150%] mb-[3rem]">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    strong: ({ children }) => (
                      <span className="font-[700] text-white">{children}</span>
                    ),
                  },
                })}
              />
            )}
          </div>
          {/* 
          <div ref={content2Ref}>
            <ul className="list-disc list-green-indicator ml-[1.5rem]">
              <li className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%]">
                {texts[language].listItems[0]}
              </li>
              <hr className="my-[1rem] border-white opacity-15" />
              <li className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%]">
                {texts[language].listItems[1]}
              </li>
              <hr className="my-[1rem] border-white opacity-15" />
              <li className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%]">
                {texts[language].listItems[2]}
              </li>
              <hr className="my-[1rem] border-white opacity-15" />
              <li className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[3rem]">
                {texts[language].listItems[3]}
              </li>
            </ul>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              <span className="orange-shadow-bg text-white text-[1.25rem] font-[400] leading-[150%] mb-[3rem]">
                {texts[language].paragraph7.substring(
                  texts[language].paragraph7.indexOf("<") + 1,
                  texts[language].paragraph7.lastIndexOf(">")
                )}
              </span>{" "}
              {texts[language].paragraph7.substring(
                texts[language].paragraph7.indexOf(">") + 1,
                texts[language].paragraph7.indexOf("{") - 1
              )}{" "}
              <span className="orange-shadow-bg text-white text-[1.25rem] font-[400] leading-[150%] mb-[3rem]">
                {texts[language].paragraph7.substring(
                  texts[language].paragraph7.indexOf("{") + 1,
                  texts[language].paragraph7.lastIndexOf("}")
                )}
              </span>{" "}
              <br />
              {texts[language].paragraph8}
            </p>

            <span className="text-white text-center text-[1.25rem] font-[600] leading-[150%] mb-[1.5rem] w-full block">
              {texts[language].subtitle5}
            </span>

            <div className="w-full grid grid-cols-2 items-stretch gap-[1.2rem] mb-[3rem]">
              <div className="flex flex-col justify-center items-center gap-[1rem] py-[1.5rem] px-[1.2rem] rounded-[1rem] bg-[#ffffff0d] transition duration-300 ease-in-out hover:scale-[1.02]">
                <Image src={icon1Img} alt="icon 1" />
                <span className="text-white text-center text-[1.25rem] font-[500] leading-[130%] tracking-[-0.0875rem] block lg:px-[0.25rem]">
                  {texts[language].cards[0]}
                </span>
              </div>
              <div className="flex flex-col justify-center items-center gap-[1rem] p-[1.5rem] rounded-[1rem] bg-[#ffffff0d] transition duration-300 ease-in-out hover:scale-[1.02]">
                <Image src={icon2Img} alt="icon 2" />
                <span className="text-white text-center text-[1.25rem] font-[500] leading-[130%] tracking-[-0.0875rem] block lg:px-[1rem]">
                  {texts[language].cards[1]}
                </span>
              </div>
              <div className="flex flex-col justify-center items-center gap-[1rem] p-[1.5rem] rounded-[1rem] bg-[#ffffff0d] transition duration-300 ease-in-out hover:scale-[1.02]">
                <Image src={icon3Img} alt="icon 3" />
                <span className="text-white text-center text-[1.25rem] font-[500] leading-[130%] tracking-[-0.0875rem] block lg:px-[0.25rem]">
                  {texts[language].cards[2]}
                </span>
              </div>
              <div className="flex flex-col justify-center items-center gap-[1rem] p-[1.5rem] rounded-[1rem] bg-[#ffffff0d] transition duration-300 ease-in-out hover:scale-[1.02]">
                <Image src={icon4Img} alt="icon 4" />
                <span className="text-white text-center text-[1.25rem] font-[500] leading-[130%] tracking-[-0.0875rem] block lg:px-[1rem]">
                  {texts[language].cards[3]}
                </span>
              </div>
            </div>
          </div>

          <div ref={content3Ref}>
            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph9}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph10}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph11}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph12.substring(
                0,
                texts[language].title.indexOf("<")
              )}{" "}
              <span className="orange-shadow-bg text-white text-[1.25rem] font-[400] leading-[150%] mb-[3rem]">
                {texts[language].paragraph12.substring(
                  texts[language].paragraph12.indexOf("<") + 1,
                  texts[language].paragraph12.lastIndexOf(">")
                )}
              </span>{" "}
              {texts[language].paragraph12.substring(
                texts[language].paragraph12.indexOf(">") + 1,
                texts[language].paragraph12.length
              )}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph13}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph14}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph15}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph16}
            </p>
          </div>

          <div
            ref={content4Ref}
            className="rounded-[1rem] p-[1.5rem] bg-[#ffffff0d] w-full mb-[2rem] hidden"
          >
            <h5 className="text-white text-[1.25rem] font-[600] leading-[150%] mb-[1.5rem]">
              {texts[language].subtitle6}
            </h5>
            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph17}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph18.substring(
                0,
                texts[language].paragraph18.indexOf("<")
              )}{" "}
              <span className="orange-shadow-bg text-white text-[1.25rem] font-[400] leading-[150%]">
                {texts[language].paragraph18.substring(
                  texts[language].paragraph18.indexOf("<") + 1,
                  texts[language].paragraph18.lastIndexOf(">")
                )}
              </span>
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph19}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph20}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph21}
            </p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]">
              {texts[language].paragraph22.substring(
                0,
                texts[language].paragraph22.indexOf("<")
              )}{" "}
              <span className="orange-shadow-bg text-white text-[1.25rem] font-[400] leading-[150%]">
                {texts[language].paragraph22.substring(
                  texts[language].paragraph22.indexOf("<") + 1,
                  texts[language].paragraph22.lastIndexOf(">")
                )}
              </span>{" "}
              {texts[language].paragraph22.substring(
                texts[language].paragraph22.indexOf(">") + 1,
                texts[language].paragraph22.indexOf("{") - 1
              )}{" "}
              <span className="orange-shadow-bg text-white text-[1.25rem] font-[400] leading-[150%]">
                {texts[language].paragraph22.substring(
                  texts[language].paragraph22.indexOf("{") + 1,
                  texts[language].paragraph22.lastIndexOf("}")
                )}
              </span>{" "}
              {texts[language].paragraph22.substring(
                texts[language].paragraph22.indexOf("}") + 1,
                texts[language].paragraph22.length
              )}
            </p>

            <p
              className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%] mb-[2rem]"
              dangerouslySetInnerHTML={{
                __html: texts[language].paragraph23,
              }}
            ></p>

            <p className="text-[#ffffffbf] text-[1.25rem] font-[400] leading-[150%]">
              {texts[language].paragraph24}
            </p>
          </div> */}

          <div className="w-full flex justify-center items-center">
            {data?.videoCTA && <CTAButton data={data?.dearCTA} />}
          </div>
        </div>
      </div>
    </section>
  );
}
